import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import WCBackground from './components/WCBackground';
import Tabs from './components/Tabs';
import MundialAddonHeader from './components/MundialAddonHeader';
import WCFixtures from './WCFixtures';
import WCStandings from './WCStandings';
import WCStatistics from './WCStatistics';
import WCCountries from './WCCountries';
import classNames from 'classnames';
import styles from './Mundial2026.module.scss';
import { SportsProvider, useSports } from './context/SportsProvider';
import { TAB_PARAM } from './utils/tabs';
import { useTabChange } from './hooks/useTabChange';
import { useIsInWebview } from './hooks/useIsInWebview';
import { useMundialUserhashLogin } from './hooks/useMundialUserhashLogin';
import {
  startEngagement,
  resetEngagement,
} from './hooks/useMundialMetrics/utils/engagementTracker';
import { useMundialMetrics } from './hooks/useMundialMetrics';
import { INTERACTION_TYPES } from './hooks/useMundialMetrics/constants';
import { getRegion } from 'utils/Region';
import { useSelector, useDispatch } from 'react-redux';
import {
  getNavData,
  getSubNodes,
} from 'containers/Menu/selectors/navData.selector';
import { nodeSelected } from 'containers/Menu/actions/menu.actions';
import { findNestedNode } from 'utils/nodes/findNestedNode';
import ContentLoader from 'containers/Player/components/ContentLoader';

const MUNDIAL_NODE_CODE = 'mundial2026';

const TABS = [
  {
    id: 'fixtures',
    label: 'eventodeportivo_pestanasecciones_texto_partidos',
    icon: 'eventodeportivo_pestanasecciones_icono_partidos_sinfoco',
    iconActive: 'eventodeportivo_pestanasecciones_icono_partidos_foco',
    iconAlt: 'Ícono de Partidos',
  },
  {
    id: 'standings',
    label: 'eventodeportivo_pestanasecciones_texto_tablaposiciones',
    icon: 'eventodeportivo_pestanasecciones_icono_tablaposiciones_sinfoco',
    iconActive: 'eventodeportivo_pestanasecciones_icono_tablaposiciones_foco',
    iconAlt: 'Ícono de Tabla de posiciones',
  },
  {
    id: 'statistics',
    label: 'eventodeportivo_pestanasecciones_texto_estadisticas',
    icon: 'eventodeportivo_pestanasecciones_icono_estadisticas_sinfoco',
    iconActive: 'eventodeportivo_pestanasecciones_icono_estadisticas_foco',
    iconAlt: 'Ícono de Estadísticas',
  },
  {
    id: 'countries',
    label: 'eventodeportivo_pestanasecciones_texto_paises',
    icon: 'eventodeportivo_pestanasecciones_icono_paises_sinfoco',
    iconActive: 'eventodeportivo_pestanasecciones_icono_paises_foco',
    iconAlt: 'Ícono de Países',
  },
];

const VIEW_MAP = {
  fixtures: WCFixtures,
  standings: WCStandings,
  statistics: WCStatistics,
  countries: WCCountries,
};

const VALID_TABS = TABS.map(t => t.id);
const DEFAULT_TAB = TABS[0].id;
const TRANSITION_DURATION = 200;

const TAB_METRIC_LABELS = {
  fixtures: 'partidos',
  standings: 'tabla de posiciones',
  statistics: 'estadisticas',
  countries: 'paises',
};

const Mundial2026Content = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { currentStage } = useSports();
  const sports_worldcup_phase2_gracenote_config = useSelector(state => {
    if (state.metadata.sports_worldcup_phase2_gracenote_config) {
      const config = JSON.parse(
        state.metadata.sports_worldcup_phase2_gracenote_config,
      );
      const region = getRegion();
      if (config[region]) {
        return config[region].enabled;
      }
      return config.default?.enabled ?? false;
    }
    return false;
  });

  const tabParam = searchParams.get(TAB_PARAM);
  const targetTab = VALID_TABS.includes(tabParam) ? tabParam : DEFAULT_TAB;

  const [displayedTab, setDisplayedTab] = useState(targetTab);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [statisticsKey, setStatisticsKey] = useState(0);
  const tabpanelRef = useRef(null);

  useEffect(() => {
    startEngagement();
    return () => {
      resetEngagement();
    };
  }, []);

  useEffect(() => {
    const mql = window.matchMedia('(min-width: 1024px)');
    const handleChange = e => {
      if (e.matches) {
        document
          .querySelector('.content_div')
          ?.scrollTo({ top: 0, behavior: 'instant' });
      }
    };
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  useEffect(
    () => {
      if (targetTab === displayedTab) return;

      setIsTransitioning(true);
      const timeoutId = setTimeout(() => {
        setDisplayedTab(targetTab);
        setIsTransitioning(false);
        if (tabpanelRef.current) {
          tabpanelRef.current.scrollTop = 0;
        }
        const contentDiv = tabpanelRef.current?.closest('.content_div');
        if (contentDiv) {
          contentDiv.scrollTop = 0;
        }
      }, TRANSITION_DURATION);

      return () => clearTimeout(timeoutId);
    },
    [targetTab, displayedTab],
  );

  const { trackSelectContent } = useMundialMetrics();

  const baseHandleTabChange = useTabChange({
    displayedTab,
    setSearchParams,
    currentStage,
  });

  const handleTabChange = useCallback(
    tabId => {
      const metricLabel = TAB_METRIC_LABELS[tabId];
      if (metricLabel) {
        trackSelectContent({
          pageTitleSegments: [metricLabel],
          pageTitle: metricLabel,
          contentSubsection: metricLabel,
          moduloName: '',
          interactionType: INTERACTION_TYPES.MAIN_TABS,
        });
      }
      if (tabId === 'statistics' && displayedTab === 'statistics') {
        setStatisticsKey(k => k + 1);
        return;
      }
      baseHandleTabChange(tabId);
    },
    [baseHandleTabChange, trackSelectContent, displayedTab],
  );

  const ActiveView = VIEW_MAP[displayedTab];

  const matchIdParam = searchParams.get('matchId');
  const showMundialAddonHeader = displayedTab === 'fixtures' && !matchIdParam;
  const isInWebview = useIsInWebview();
  const isMatchDetailView = searchParams.has('matchId');

  return (
    <div
      className={styles['mundial2026']}
      data-role="mundial2026"
      {...(isInWebview ? { 'data-in-webview': '' } : {})}
    >
      <div
        ref={tabpanelRef}
        className={`${styles['mundial2026__tabpanel']} ${
          isMatchDetailView ? styles['mundial2026__tabpanel__match'] : ''
        }`}
        data-role="mundial2026-tabpanel"
      >
        {showMundialAddonHeader ? <MundialAddonHeader /> : null}
        {sports_worldcup_phase2_gracenote_config && (
          <Tabs
            tabs={TABS}
            activeTab={displayedTab}
            onChange={handleTabChange}
          />
        )}
        <div
          className={classNames(styles['mundial2026__tabpanel-content'], {
            [styles[
              'mundial2026__tabpanel-content--transitioning'
            ]]: isTransitioning,
            [styles['mundial2026__tabpanel-content--in-webview']]: isInWebview,
          })}
          role="tabpanel"
          id={`panel-${displayedTab}`}
          aria-labelledby={`tab-${displayedTab}`}
        >
          {ActiveView && (
            <ActiveView
              key={displayedTab === 'statistics' ? statisticsKey : undefined}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const Mundial2026 = () => {
  const dispatch = useDispatch();
  const nodes = useSelector(getNavData);
  const selectedNode = useSelector(getSubNodes);
  const { status: userhashLoginStatus } = useMundialUserhashLogin();

  useEffect(
    () => {
      if (!nodes || !Array.isArray(nodes) || nodes.length === 0) return;
      if (selectedNode?.code === MUNDIAL_NODE_CODE) return;

      const mundialNode =
        nodes.find(node => node?.code === MUNDIAL_NODE_CODE) ||
        findNestedNode(nodes, 'code', MUNDIAL_NODE_CODE);

      if (mundialNode) dispatch(nodeSelected(mundialNode));
    },
    [nodes, selectedNode?.code],
  );

  if (userhashLoginStatus === 'pending') return <ContentLoader />;

  return (
    <SportsProvider>
      <Mundial2026Content />
    </SportsProvider>
  );
};

export default Mundial2026;
